import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import clsx from 'clsx';
import styles from './searchableSelect.module.scss';

const isPrimitiveOption = option =>
  typeof option === 'string' || typeof option === 'number';

const normalizeOption = (option, labelKey, valueKey) => {
  if (isPrimitiveOption(option)) {
    const normalized = String(option);
    return { label: normalized, value: normalized, key: normalized, raw: option };
  }

  const label = option[labelKey] ?? option.title ?? String(option);
  const value = option[valueKey] ?? option.title ?? String(option);
  const key = option.id ?? value;

  return { label, value, key, raw: option };
};

const SearchableSelect = ({
  options = [],
  value = '',
  onChange,
  onBlur,
  name,
  placeholder,
  className,
  disabled = false,
  readOnly = false,
  noResultsText = 'Ничего не найдено',
  labelKey = 'title',
  valueKey = 'title',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef(null);

  const normalizedOptions = useMemo(
    () => options.map(option => normalizeOption(option, labelKey, valueKey)),
    [options, labelKey, valueKey]
  );

  const selectedOption = useMemo(
    () => normalizedOptions.find(option => option.value === value),
    [normalizedOptions, value]
  );

  useEffect(() => {
    setQuery(selectedOption?.label ?? value ?? '');
  }, [selectedOption, value]);

  const filteredOptions = useMemo(() => {
    if (readOnly) return normalizedOptions;
    const normalized = query.trim().toLowerCase();
    if (!normalized) return normalizedOptions;
    return normalizedOptions.filter(option =>
      option.label.toLowerCase().includes(normalized)
    );
  }, [normalizedOptions, query, readOnly]);

  const findOptionByLabel = useCallback(
    label => normalizedOptions.find(option => option.label === label),
    [normalizedOptions]
  );

  const commitValue = useCallback(
    nextValue => {
      onChange?.(nextValue);
    },
    [onChange]
  );

  useEffect(() => {
    const handlePointerDown = event => {
      if (!wrapperRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  const handleInputChange = event => {
    if (readOnly) return;
    const nextQuery = event.target.value;
    setQuery(nextQuery);
    setOpen(true);
    const matchedOption = findOptionByLabel(nextQuery);
    commitValue(matchedOption ? matchedOption.value : '');
  };

  const handleSelect = option => {
    setQuery(option.label);
    commitValue(option.value);
    setOpen(false);
  };

  const handleBlur = () => {
    window.setTimeout(() => {
      const matchedOption = findOptionByLabel(query);
      if (matchedOption) {
        commitValue(matchedOption.value);
      } else {
        setQuery(selectedOption?.label ?? value ?? '');
      }
      onBlur?.();
    }, 0);
  };

  return (
    <div ref={wrapperRef} className={clsx(styles.wrapper, className)}>
      <input
        type="text"
        className={styles.input}
        name={name}
        value={query}
        placeholder={placeholder}
        onChange={handleInputChange}
        onFocus={() => setOpen(true)}
        onClick={readOnly ? () => setOpen(true) : undefined}
        onBlur={handleBlur}
        disabled={disabled}
        readOnly={readOnly}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
      />
      <button
        type="button"
        className={styles.toggle}
        onClick={() => setOpen(prev => !prev)}
        disabled={disabled}
        tabIndex={-1}
        aria-label="Открыть список"
      >
        <svg
          className={clsx(styles.chevron, open && styles.chevronOpen)}
          width="12"
          height="8"
          viewBox="0 0 12 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </button>
      {open && (
        <ul className={styles.list} role="listbox">
          {filteredOptions.length > 0 ? (
            filteredOptions.map(option => (
              <li
                key={option.key}
                className={clsx(styles.option, option.value === value && styles.optionActive)}
                role="option"
                aria-selected={option.value === value}
                onMouseDown={event => event.preventDefault()}
                onClick={() => handleSelect(option)}
              >
                {option.label}
              </li>
            ))
          ) : (
            <li className={styles.empty}>{noResultsText}</li>
          )}
        </ul>
      )}
    </div>
  );
};

export default SearchableSelect;
